import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";

import { releaseParkedEvents } from "@/anticheat";
import { useContentProtection } from "@/anticheat/react/useContentProtection";
import { useSnapshotSchedule } from "@/anticheat/react/useSnapshotSchedule";
import {
  AlertCircle,
  CheckCircle2,
  ChevronRight,
  Clock,
  Loader2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { CameraPreview } from "@/components/candidate/CameraPreview";
import {
  CAMERA_TILE_FOOTER_CLEAR,
  CAMERA_TILE_RESERVE,
  snapshotCaption,
  useCameraTileShown,
} from "@/components/candidate/cameraTile";
import { ReportProblemTrigger } from "@/components/candidate/ReportProblemDialog";
import {
  SUBMIT_FAILED_MESSAGE,
  SubmitConfirmDialog,
} from "@/components/candidate/SubmitConfirmDialog";
import {
  ApiError,
  bounceToGate,
  fetchReview,
  loadAttemptId,
  loadCandidateMeta,
  submitAttempt,
  type ReviewResponse,
} from "@/lib/api";
import { AnswerSaveFailureNote } from "@/components/candidate/AnswerSaveFailureNote";
import { flushAnswerSaves } from "@/lib/answerQueue";
import { useAnswerSaveFailures } from "@/lib/useAnswerSaveFailures";
import { flushReplayUploads } from "@/lib/codeReplayRecorder";
import { isAttemptGone, pausedAttemptOf } from "@/lib/candidateError";
import { queryKeys } from "@/lib/queryKeys";
import { submitSummary } from "@/lib/submitConfirm";
import { useCountdown } from "@/lib/useCountdown";
import { cn, formatMMSS, stripMarkdown } from "@/lib/utils";
import { questionTypeIcon } from "@/lib/questionTypeIcon";
import { pausedStateOf } from "./Paused";

export default function Review() {
  const { token = "demo" } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const attemptId = loadAttemptId(token);
  // FEAT-17: the prompt previews are question content too.
  useContentProtection(Boolean(attemptId));

  // EF-8 (photo cadence, 2026-10-10): the clock runs here, so the attempt's
  // ONE 30 s snapshot schedule carries on — held from this page exactly as
  // the question page holds it, from the schedule the attempt froze (the
  // tab's meta; GET /review has none). No controller, no detector: nothing
  // else is recorded on this screen. The tile with its caption follows.
  const snapshots = useMemo(
    () => loadCandidateMeta(token)?.camera_snapshots ?? null,
    [token],
  );
  useSnapshotSchedule(token, attemptId, snapshots, true);
  const tileShown = useCameraTileShown() && snapshots !== null;

  // Pre-ship R7: no anti-cheat controller lives here to adopt what the last
  // question parked (anticheat `parked`, a 3 s hand-off), and a Submit inside
  // that window graded the attempt — verdict, webhook, Traffit note — before
  // those events landed. Ship them on arrival, as dispose() did before parking.
  useEffect(() => {
    releaseParkedEvents();
  }, []);

  useEffect(() => {
    if (!attemptId) navigate(`/t/${token}`, { replace: true, state: { from: "bounce" } });
  }, [attemptId, navigate, token]);

  // Drain any background answer saves BEFORE fetching the review, so the
  // answered/required pills (and the Submit gate they drive) reflect the
  // candidate's just-enqueued answers rather than pre-save server state. The
  // query stays disabled until this resolves — the existing loading spinner
  // covers the brief wait (usually already drained on arrival).
  const [savesFlushed, setSavesFlushed] = useState(false);
  useEffect(() => {
    let alive = true;
    void flushAnswerSaves().finally(() => {
      if (alive) setSavesFlushed(true);
    });
    return () => {
      alive = false;
    };
  }, []);

  const { data, isLoading, error, refetch } = useQuery<ReviewResponse>({
    queryKey: queryKeys.review(token, attemptId),
    queryFn: () => fetchReview(token, attemptId!),
    enabled: Boolean(attemptId) && savesFlushed,
    staleTime: 5_000,
  });

  // Submitted/expired → Done. Classified at render rather than only inside the
  // effect because the error card below needs it too: the redirect lives in an
  // effect that runs after paint, so without this the "Couldn't load your
  // answers" card flashes for one frame on the way out. Verified 2026-08-26 —
  // it really did paint. SectionReview.tsx has always carved this out; Review
  // lost the carve-out when the guards were reordered in 17f059e.
  // EF-2: a paused attempt answers 409 `attempt_paused` here — the one 409
  // that leaves a LIVE attempt behind. It goes to the paused screen (rebuilt
  // from the 409), never to Done.
  const pausedAttempt = pausedAttemptOf(error);
  const attemptFinalized =
    !pausedAttempt &&
    error instanceof ApiError &&
    (error.status === 409 || error.status === 410);

  // Keyed on `error` (stable per failure), not on the fresh object above, so
  // the redirect fires once per failure rather than once per render.
  useEffect(() => {
    const paused = pausedAttemptOf(error);
    if (!paused) return;
    navigate(`/t/${token}/paused`, {
      replace: true,
      state: pausedStateOf(paused),
    });
  }, [error, navigate, token]);

  useEffect(() => {
    if (!error || !attemptFinalized) return;
    navigate(`/t/${token}/done`, { replace: true });
  }, [error, attemptFinalized, navigate, token]);

  // 10-07 S11: voided (an invitation replaced this link attempt, or a retake)
  // or erased — the stored id is junk; back to the Gate, never the load card.
  const attemptGone = isAttemptGone(error);
  useEffect(() => {
    if (!attemptGone) return;
    bounceToGate(token, navigate);
  }, [attemptGone, navigate, token]);

  // Defensive backstop: a sectioned attempt must never use this global
  // jump-anywhere review (past sections are locked). Question.tsx routes
  // sectioned attempts to /section-review instead, so this only fires on a
  // hand-typed/stale URL — bounce to the gate, which resumes the attempt.
  useEffect(() => {
    if (data?.sections_enabled) {
      navigate(`/t/${token}`, { replace: true, state: { from: "bounce" } });
    }
  }, [data?.sections_enabled, navigate, token]);

  // Countdown — same as Question header so candidates don't forget time pressure
  // mid-review. EF-9/FEAT-11: a clockless attempt has none to forget, and its
  // `expires_at` is the hidden 24 h safety bound — rendering it put a live
  // "1439:58" on the candidate's last screen.
  const remainingMs = useCountdown(data?.untimed ? undefined : data?.expires_at);

  // When the timer expires here, treat it the same as the Question page does —
  // submit the attempt as-is and go to Done.
  const questionnaireStartsAt = data?.questionnaire_starts_at ?? null;
  useEffect(() => {
    if (remainingMs !== 0 || !attemptId) return;
    void (async () => {
      // Land pending saves before the timer-driven submit so grading sees them.
      await flushAnswerSaves().catch(() => {});
      // FEAT-31: and the last chunk of a code replay (bounded, never throws).
      await flushReplayUploads();
      // FEAT-11 — the clock that ran out is the TECHNICAL half's and a
      // clockless questionnaire is still owed, so hand the candidate on
      // instead of submitting. Exactly what Question.tsx's handleTimeUp does;
      // without it, sitting on Review while the clock runs out is the one way
      // left to lose the profile the recruiter sent the test to collect.
      // (`untimed` is already true once they have crossed, so the countdown
      // never reaches zero on that side of the boundary.)
      if (questionnaireStartsAt != null) {
        navigate(`/t/${token}/question/${questionnaireStartsAt}`, {
          replace: true,
        });
        return;
      }
      await submitAttempt(token, attemptId).catch(() => {});
      navigate(`/t/${token}/done`, { replace: true });
    })();
  }, [remainingMs, attemptId, navigate, token, questionnaireStartsAt]);

  // FEAT-15: "Submit final" asks first. The clock-driven submit above never
  // does — the clock decided.
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // OPS-28 #1: answers the queue gave up on. While one exists the Submit is
  // held — grading now would score those positions as blank while every pill
  // here says "answered" — and the note offers the retry. The clock-driven
  // submit above is NOT held (the clock decided; the pre-deadline save
  // carried what it could).
  const saveFailures = useAnswerSaveFailures(attemptId);

  const submitMutation = useMutation({
    // Guarantee every background answer save has landed before /submit triggers
    // grading — the final submit lives on this screen, not on Question.
    mutationFn: async () => {
      await flushAnswerSaves();
      // FEAT-31: /submit seals the attempt — a replay chunk landing after it
      // is refused. Bounded (1.5 s) and never throws.
      await flushReplayUploads();
      return submitAttempt(token, attemptId!);
    },
    onSuccess: () => navigate(`/t/${token}/done`, { replace: true }),
    onError: (err) => {
      // Already finalized elsewhere → converge to Done. A 409 required_unanswered
      // is normally pre-empted by the disabled button; refetch so the pills
      // reflect server truth if the local data was stale. Classify on status
      // (ApiError), not the message text — 410-ONLY here on purpose: a 409 must
      // refetch, never route to Done.
      if (err instanceof ApiError && err.status === 410) {
        navigate(`/t/${token}/done`, { replace: true });
        return;
      }
      // 10-07 S11: the attempt is gone (voided / erased) — back to the Gate.
      if (isAttemptGone(err)) {
        bounceToGate(token, navigate);
        return;
      }
      // FEAT-15: a 409 is the server disagreeing with this screen, so the
      // confirm closes and the refetched pills say why. Anything else (a
      // network blip, a 5xx) is retryable from the dialog itself.
      if (err instanceof ApiError && err.status === 409) {
        setSubmitConfirmOpen(false);
      } else {
        setSubmitError(SUBMIT_FAILED_MESSAGE);
      }
      void refetch();
    },
  });

  if (!attemptId) return null;

  // Error BEFORE the loading/!data guard — React Query leaves `data` undefined
  // on a failed fetch, so the reverse order renders the spinner forever and the
  // candidate never sees this card. Same drift fixed in Question.tsx 2026-08-26;
  // SectionReview.tsx has always had the correct order.
  // FEAT-15 verify (2026-10-03): only when there is nothing to show. A failed
  // REFETCH keeps the answers already on screen — the submit's own refetch
  // behind the confirm (found in the browser: a 5xx submit with the network
  // down said "try again", then this card took the confirm away ~7 s later),
  // or a window-focus refetch during a blip.
  if (attemptGone) return null;
  if (error && !attemptFinalized && !pausedAttempt && !data && !submitMutation.isPending) {
    return (
      <div className="flex min-h-full items-center justify-center bg-background p-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Couldn't load your answers</CardTitle>
            <CardDescription>
              Try refreshing. If the issue persists, contact the recruiter.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="flex min-h-full items-center justify-center bg-background p-6">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const total = data.answers.length;
  const allowJumpBack = data.allow_back_navigation;
  // Free navigation: per-question saves are drafts, so "done" tracks ANSWERED,
  // not submitted. The Required gate (server-enforced at /submit) is the real
  // ship blocker — surface it here and disable the final submit.
  const answeredCount = data.answers.filter((a) => a.has_answer).length;
  // FEAT-11: a CLOSED row (a technical question on a mixed attempt whose
  // candidate has crossed into the questionnaire) can no longer be answered,
  // so it cannot block the submit either — the server applies exactly this
  // exemption, and without the same rule here the button stays disabled on an
  // attempt the server would happily take: both results collected, no way in.
  const requiredUnanswered = data.answers.filter(
    (a) => a.answer_required && !a.has_answer && a.editable !== false,
  );
  const unsavedAnswers = saveFailures.failures.length;
  const canSubmit = requiredUnanswered.length === 0 && unsavedAnswers === 0;
  const firstRequiredUnanswered = requiredUnanswered[0];
  const firstUnanswered = data.answers.find(
    (a) => !a.has_answer && a.editable !== false,
  );

  return (
    <div className="flex h-full flex-col bg-background">
      {snapshots && (
        <CameraPreview caption={snapshotCaption(snapshots.interval_s)} />
      )}
      {submitConfirmOpen && (
        <SubmitConfirmDialog
          summary={submitSummary(data.answers)}
          // Same formatter as the header clock below, so the two agree; no
          // line at all on a clockless attempt.
          timeLeft={remainingMs !== null ? formatMMSS(remainingMs) : null}
          onConfirm={() => submitMutation.mutate()}
          onCancel={() => setSubmitConfirmOpen(false)}
          isPending={submitMutation.isPending}
          error={submitError}
        />
      )}
      <header className="flex items-center justify-between border-b border-border bg-card px-6 py-3">
        <div className="text-sm">
          <span className="font-medium text-foreground">Review</span>
          <span className="ml-2 text-muted-foreground">{data.test_name}</span>
        </div>
        {remainingMs !== null && (
          <div
            className={cn(
              "flex items-center gap-1.5 font-mono text-sm tabular-nums",
              remainingMs <= 60_000
                ? "font-semibold text-destructive"
                : "text-muted-foreground",
            )}
            role="timer"
            aria-label="Time remaining"
          >
            <Clock className="h-4 w-4" aria-hidden="true" />
            {formatMMSS(remainingMs)}
          </div>
        )}
      </header>

      {/* The camera tile is a fixed thumbnail bottom-right: room under the
          last card so its Edit button scrolls clear, and the sticky footer's
          right end — "Submit final" — kept out from under it. */}
      <main
        className={cn(
          "mx-auto w-full max-w-3xl flex-1 space-y-6 px-6 py-8",
          tileShown && CAMERA_TILE_RESERVE,
        )}
      >
        <AnswerSaveFailureNote
          failures={saveFailures.failures}
          refused={saveFailures.refused}
          requiredPositions={requiredUnanswered.map((a) => a.position)}
          onRetry={() => void saveFailures.retry().then(() => refetch())}
          retrying={saveFailures.retrying}
        />
        <SummaryBanner
          answered={answeredCount}
          total={total}
          requiredUnanswered={requiredUnanswered.length}
        />
        <div className="space-y-3">
          {data.answers.map((ans) => (
            <ReviewCard
              key={ans.position}
              answer={ans}
              questionnaireStartsAt={questionnaireStartsAt}
              // FEAT-11: a technical row is CLOSED once the candidate has
              // crossed into the questionnaire — the server refuses the write
              // and answerQueue swallows the failure, so an Edit offered here
              // would lose the answer silently.
              allowJumpBack={allowJumpBack && ans.editable !== false}
              onJumpBack={() =>
                navigate(`/t/${token}/question/${ans.position}`)
              }
            />
          ))}
        </div>
      </main>

      <footer
        className={cn(
          "sticky bottom-0 border-t border-border bg-card px-6 py-3",
          tileShown && CAMERA_TILE_FOOTER_CLEAR,
        )}
      >
        <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-3">
          {allowJumpBack && firstRequiredUnanswered ? (
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                navigate(
                  `/t/${token}/question/${firstRequiredUnanswered.position}`,
                )
              }
              className="cursor-pointer"
            >
              Answer required{" "}
              {reviewBadge(firstRequiredUnanswered.position, questionnaireStartsAt)}
            </Button>
          ) : allowJumpBack && firstUnanswered ? (
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                navigate(`/t/${token}/question/${firstUnanswered.position}`)
              }
              className="cursor-pointer"
            >
              Continue editing{" "}
              {reviewBadge(firstUnanswered.position, questionnaireStartsAt)}
            </Button>
          ) : (
            <span className="text-sm text-muted-foreground">
              {canSubmit
                ? "Ready to submit."
                : unsavedAnswers > 0
                  ? "Retry the unsaved answers to submit."
                  : "Complete the required questions to submit."}
            </span>
          )}
          <div className="flex items-center gap-4">
            <ReportProblemTrigger token={token} attemptId={attemptId} />
            <Button
              type="button"
              onClick={() => {
                setSubmitError(null);
                setSubmitConfirmOpen(true);
              }}
              disabled={submitMutation.isPending || !canSubmit}
              className="cursor-pointer"
            >
              {submitMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Submitting…
                </>
              ) : (
                "Submit final"
              )}
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
}

function SummaryBanner({
  answered,
  total,
  requiredUnanswered,
}: {
  answered: number;
  total: number;
  requiredUnanswered: number;
}) {
  const blocked = requiredUnanswered > 0;
  const allAnswered = answered === total;
  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-md border p-4",
        blocked
          ? "border-destructive/30 bg-destructive/5"
          : "border-success/40 bg-success/5",
      )}
    >
      {blocked ? (
        <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-destructive" />
      ) : (
        <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-success" />
      )}
      <div className="space-y-1">
        <p
          className={cn(
            "text-sm font-medium",
            blocked ? "text-destructive" : "text-success",
          )}
        >
          {blocked
            ? `${requiredUnanswered} required question${requiredUnanswered === 1 ? "" : "s"} still ${requiredUnanswered === 1 ? "needs" : "need"} an answer.`
            : allAnswered
              ? total === 1
                ? "You've answered the question."
                : `You've answered all ${total} questions.`
              : `${answered} of ${total} answered.`}
        </p>
        <p className="text-xs text-muted-foreground">
          {blocked
            ? "Answer every required question before submitting."
            : "Review your answers, then submit when you're ready."}
        </p>
      </div>
    </div>
  );
}

/**
 * FEAT-11: a questionnaire statement is badged S1…Sn, counted from where the
 * questionnaire starts — the same local number the question screen shows
 * ("Statement 1 of 9"). Technical questions keep their global Q-number.
 */
export function reviewBadge(
  position: number,
  questionnaireStartsAt: number | null | undefined,
): string {
  return questionnaireStartsAt != null && position >= questionnaireStartsAt
    ? `S${position - questionnaireStartsAt + 1}`
    : `Q${position}`;
}

export function ReviewCard({
  answer,
  allowJumpBack,
  onJumpBack,
  questionnaireStartsAt = null,
}: {
  answer: {
    position: number;
    type: string;
    prompt_preview: string;
    submitted: boolean;
    has_answer: boolean;
    answer_required: boolean;
  };
  allowJumpBack: boolean;
  onJumpBack: () => void;
  /** FEAT-11: where the questionnaire starts, for the S-badge. */
  questionnaireStartsAt?: number | null;
}) {
  const Icon = questionTypeIcon(answer.type);
  const typeLabel =
    answer.type === "mcq"
      ? "Multiple choice"
      : answer.type === "mcq_multi"
        ? "Multi-correct"
        : answer.type === "true_false"
          ? "True/false"
          : answer.type === "open_ended"
            ? "Written answer"
            : answer.type === "file_upload"
              ? "File upload"
              : answer.type === "likert"
                ? "Statement"
                : "Code";

  return (
    <div className="flex items-start gap-4 rounded-md border border-border bg-card p-4 md:items-center">
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-secondary font-mono text-sm font-semibold text-secondary-foreground">
        {reviewBadge(answer.position, questionnaireStartsAt)}
      </div>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-2">
          <Icon className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          <span className="text-xs uppercase tracking-wide text-muted-foreground">
            {typeLabel}
          </span>
          <StatusPill answer={answer} />
        </div>
        {/* Three lines on a phone, one on a wide screen. At 375 a single
            clamped line left ~20 characters — "A team runs a statele…" — on
            the screen where a candidate decides what to go back and change,
            inside a card with free vertical space below it (2026-09-21 design
            review). Above md the row is wide enough that one line is the
            whole stem, and the scannable list is worth more there. */}
        <p className="line-clamp-3 text-sm text-foreground md:line-clamp-1">
          {stripMarkdown(answer.prompt_preview)}
        </p>
      </div>
      {allowJumpBack ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onJumpBack}
          className="cursor-pointer"
        >
          {answer.has_answer ? "Edit" : "Answer"}
          <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      ) : (
        <span className="text-xs text-muted-foreground">
          {answer.submitted
            ? "Submitted"
            : answer.has_answer
              ? "Answered"
              : "—"}
        </span>
      )}
    </div>
  );
}

export function StatusPill({
  answer,
}: {
  answer: { has_answer: boolean; answer_required: boolean };
}) {
  if (answer.has_answer) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md border border-success/30 bg-success/5 px-1.5 py-0.5 text-4xs font-medium text-success">
        Answered
      </span>
    );
  }
  if (answer.answer_required) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md border border-destructive/30 bg-destructive/5 px-1.5 py-0.5 text-4xs font-medium text-destructive">
        Required
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-muted-foreground/30 bg-muted/30 px-1.5 py-0.5 text-4xs font-medium text-muted-foreground">
      Optional
    </span>
  );
}
